import React, { useState, ReactNode, useRef, useImperativeHandle, forwardRef, useEffect, useCallback } from 'react';

export interface WrappedNavigationRef {
  setCanNavigate: (can: boolean) => void;
}

interface WrappedScreenWrapperProps {
  screens: ReactNode[];
  slideDurations?: number[]; // Optional array of durations in ms for each slide
  onComplete?: () => void;
}

export const WrappedScreenWrapper = forwardRef<WrappedNavigationRef, WrappedScreenWrapperProps>(({
  screens,
  slideDurations,
  onComplete,
}, ref) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canNavigate, setCanNavigate] = useState(true);
  const [hasStarted, setHasStarted] = useState(false); // Track if user has started the wrapped
  const [progressWidths, setProgressWidths] = useState<number[]>([]);
  const [animatingIndex, setAnimatingIndex] = useState<number>(-1); // Track which slide is currently animating
  const [slideDirection, setSlideDirection] = useState<'forward' | 'backward'>('forward'); // Track slide direction for animations
  const [isAudioMuted, setIsAudioMuted] = useState(true); // Track if audio is muted (show button when muted)
  const autoAdvanceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const DEFAULT_SLIDE_DURATION = 4000; // 4 seconds per slide (typical for wrapped/stories)
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasPlayedAudioRef = useRef<boolean>(false);
  const lastNavigationTimeRef = useRef<number>(0); // Prevent rapid navigation
  const userExplicitlyMutedRef = useRef<boolean>(false); // Track if user explicitly muted via button
  
  // Prevent screen from sleeping during wrapped playback
  useEffect(() => {
    let wakeLock: any = null;
    
    const requestWakeLock = async () => {
      try {
        // Check if Wake Lock API is supported
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          console.log('[DIAG] Wake lock acquired');
          
          // Handle wake lock release (e.g., when user switches tabs)
          wakeLock.addEventListener('release', () => {
            console.log('[DIAG] Wake lock released');
          });
        }
      } catch (err) {
        // Wake Lock API not supported or failed (e.g., user denied permission)
        console.log('[DIAG] Wake lock not available:', err);
      }
    };
    
    // Request wake lock when component mounts
    requestWakeLock();
    
    // Cleanup: release wake lock when component unmounts
    return () => {
      if (wakeLock) {
        wakeLock.release().then(() => {
          console.log('[DIAG] Wake lock released on unmount');
        }).catch(() => {});
      }
    };
  }, []);
  
  // Diagnostic mode: disable overlay to test if it's blocking interactions
  // Set to true via window.__DISABLE_WRAPPED_OVERLAY = true in console
  const [overlayDisabled, setOverlayDisabled] = useState(false);
  
  useEffect(() => {
    // Check for diagnostic flag
    const checkOverlayDisabled = () => {
      const disabled = (window as any).__DISABLE_WRAPPED_OVERLAY === true;
      setOverlayDisabled(disabled);
    };
    
    checkOverlayDisabled();
    const interval = setInterval(checkOverlayDisabled, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  // Get duration for current slide
  const getSlideDuration = useCallback((index: number): number => {
    if (slideDurations && slideDurations[index] !== undefined) {
      return slideDurations[index];
    }
    return DEFAULT_SLIDE_DURATION;
  }, [slideDurations]);

  useImperativeHandle(ref, () => ({
    setCanNavigate,
  }));

  const handleNext = useCallback(() => {
    if (!canNavigate) return;
    
    // Prevent rapid navigation (debounce)
    const now = Date.now();
    if (now - lastNavigationTimeRef.current < 300) {
      console.log('[DIAG] Navigation debounced - too soon after last navigation');
      return;
    }
    lastNavigationTimeRef.current = now;
    
    // Clear any pending auto-advance timer to prevent double-advance
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    
    setSlideDirection('forward');
    setCurrentIndex(prev => {
      if (prev < screens.length - 1) {
        return prev + 1;
      } else if (onComplete) {
        onComplete();
      }
      return prev;
    });
  }, [canNavigate, screens.length, onComplete]);


  const handlePrev = useCallback(() => {
    if (!canNavigate) return;
    
    setSlideDirection('backward');
    setCurrentIndex(prev => {
      if (prev > 0) {
        return prev - 1;
      }
      return prev;
    });
  }, [canNavigate]);

  // Handle audio toggle button click
  const handleAudioToggle = useCallback(() => {
    if (audioRef.current) {
      // If audio is paused (including on first slide), start playing it
      if (audioRef.current.paused) {
        // Start playing audio if not playing yet
        console.log('[DIAG] Starting audio from button click (paused state)');
        audioRef.current.muted = false;
        audioRef.current.play()
          .then(() => {
            console.log('[DIAG] Audio started from button');
            hasPlayedAudioRef.current = true;
            setIsAudioMuted(false);
            userExplicitlyMutedRef.current = false; // User unmuted via button
          })
          .catch((error) => {
            console.log('[DIAG] Audio start failed, trying muted:', error);
            // Try muted if unmuted fails
            if (audioRef.current) {
              audioRef.current.muted = true;
              audioRef.current.play()
                .then(() => {
                  hasPlayedAudioRef.current = true;
                  setIsAudioMuted(true);
                  // Unmute after successful play
                  setTimeout(() => {
                    if (audioRef.current) {
                      audioRef.current.muted = false;
                      setIsAudioMuted(false);
                      userExplicitlyMutedRef.current = false;
                    }
                  }, 100);
                })
                .catch(() => {});
            }
          });
      } else {
        // Audio is playing - toggle mute/unmute
        // Read current state directly from audio element to ensure accuracy
        const currentMutedState = audioRef.current.muted;
        const newMutedState = !currentMutedState;
        // Set the muted state on the audio element first
        audioRef.current.muted = newMutedState;
        // Track user's explicit choice - only track if muting, clear if unmuting
        if (newMutedState) {
          userExplicitlyMutedRef.current = true; // User explicitly muted
        } else {
          userExplicitlyMutedRef.current = false; // User explicitly unmuted, allow auto-unmute again
        }
        // Update state immediately to reflect the change
        setIsAudioMuted(newMutedState);
        console.log('[DIAG] Audio toggled from', currentMutedState ? 'muted' : 'unmuted', 'to', newMutedState ? 'muted' : 'unmuted', 'userExplicitlyMuted:', userExplicitlyMutedRef.current);
      }
    } else {
      console.log('[DIAG] Audio ref is null, cannot toggle');
    }
  }, []);
  
  // Sync button state with actual audio muted state periodically
  // But don't override user's explicit mute choice
  useEffect(() => {
    const syncState = () => {
      if (audioRef.current) {
        const actualMuted = audioRef.current.muted;
        // If states are out of sync
        if (actualMuted !== isAudioMuted) {
          // If user explicitly muted, don't let sync override it by unmuting
          // But if audio got muted externally and user didn't explicitly mute, sync it
          if (userExplicitlyMutedRef.current && !actualMuted) {
            // User explicitly muted, but audio got unmuted - re-mute it
            audioRef.current.muted = true;
            console.log('[DIAG] Re-muting audio - user explicitly muted');
          } else if (!userExplicitlyMutedRef.current) {
            // User didn't explicitly mute, so sync with actual state
            setIsAudioMuted(actualMuted);
          }
        }
      }
    };
    
    // Sync immediately
    syncState();
    
    // Sync periodically to catch any external changes
    const interval = setInterval(syncState, 200);
    
    return () => clearInterval(interval);
  }, [isAudioMuted]);

  const canGoNext = canNavigate && currentIndex < screens.length - 1;
  const canGoPrev = canNavigate && currentIndex > 0;

  // Helper function to handle clicking interactive elements
  const handleInteractiveElementClick = (element: HTMLElement, e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If it's a link with target="_blank", use window.open to avoid popup blocker
    if (element.tagName === 'A') {
      const link = element as HTMLAnchorElement;
      if (link.target === '_blank' && link.href) {
        console.log('[DIAG] Opening link in new window:', link.href);
        window.open(link.href, '_blank', 'noopener,noreferrer');
        return;
      }
    }
    
    // For buttons and other elements, trigger click immediately
    element.click();
  };

  // Track the previous index to detect direction of navigation
  const prevIndexRef = useRef<number>(0);

  // Initialize audio element on mount
  useEffect(() => {
    // Create and preload audio element
    if (!audioRef.current) {
      console.log('[DIAG] Creating audio element');
      audioRef.current = new Audio('/pony-sample.mp3');
      audioRef.current.volume = 0.7; // Set volume to 70%
      audioRef.current.preload = 'auto';
      audioRef.current.loop = true; // Loop the audio so it continues playing
      audioRef.current.muted = true; // Start muted for autoplay (will unmute after user interaction)
      
      // Add event listeners for debugging
      audioRef.current.addEventListener('loadstart', () => {
        console.log('[DIAG] Audio loadstart');
      });
      audioRef.current.addEventListener('canplay', () => {
        console.log('[DIAG] Audio canplay');
      });
      audioRef.current.addEventListener('play', () => {
        console.log('[DIAG] Audio playing');
      });
      audioRef.current.addEventListener('error', (e) => {
        console.error('[DIAG] Audio error:', e);
        console.error('[DIAG] Audio error details:', audioRef.current?.error);
      });
      
      // Add fade-out effect in last 2 seconds before loop
      let lastTime = 0;
      audioRef.current.addEventListener('timeupdate', () => {
        if (audioRef.current && !audioRef.current.paused && !audioRef.current.muted) {
          const duration = audioRef.current.duration;
          const currentTime = audioRef.current.currentTime;
          
          // Detect if audio looped back to start (time went backwards)
          if (currentTime < lastTime && currentTime < 1) {
            // Audio looped, reset volume
            audioRef.current.volume = 0.7;
          }
          lastTime = currentTime;
          
          if (duration && duration > 0 && currentTime > 0) {
            const fadeStartTime = duration - 2; // Start fading 2 seconds before end
            const baseVolume = 0.7; // Original volume
            
            if (currentTime >= fadeStartTime) {
              // Calculate fade: 0% at end, 100% at fadeStartTime
              const fadeProgress = (duration - currentTime) / 2; // 0 to 1 over 2 seconds
              audioRef.current.volume = baseVolume * Math.max(0, Math.min(1, fadeProgress));
            } else if (currentTime < fadeStartTime) {
              // Reset to full volume if not in fade zone
              audioRef.current.volume = baseVolume;
            }
          }
        }
      });
    }
    
    return () => {
      // Cleanup audio on unmount only
      if (audioRef.current) {
        console.log('[DIAG] Cleaning up audio');
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []); // Only run on mount

  // Play audio after first slide
  useEffect(() => {
    console.log('[DIAG] currentIndex changed to:', currentIndex, 'hasPlayedAudio:', hasPlayedAudioRef.current, 'hasStarted:', hasStarted);
    
    // Play audio when transitioning to second slide (index 1)
    if (currentIndex === 1 && !hasPlayedAudioRef.current) {
      console.log('[DIAG] Attempting to play audio after first slide');
      
      if (audioRef.current) {
        // Try to play unmuted first (if user has interacted, this should work)
        audioRef.current.muted = false;
        console.log('[DIAG] Audio readyState:', audioRef.current.readyState);
        
        // Try to play immediately, or wait for canplay
        const tryPlay = () => {
          if (audioRef.current && !hasPlayedAudioRef.current) {
            hasPlayedAudioRef.current = true;
            console.log('[DIAG] Attempting to play audio unmuted, muted:', audioRef.current.muted);
            const playPromise = audioRef.current.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('[DIAG] Audio play succeeded (unmuted), playing:', !audioRef.current?.paused);
                  // Audio is now playing unmuted
                  setIsAudioMuted(false);
                })
                .catch((error) => {
                  console.log('[DIAG] Audio autoplay prevented (unmuted), trying muted:', error);
                  // If unmuted fails, try muted as fallback
                  if (audioRef.current) {
                    audioRef.current.muted = true;
                    audioRef.current.play()
                      .then(() => {
                        console.log('[DIAG] Audio play succeeded (muted), will unmute on user interaction');
                        // Will unmute on next user interaction
                        setIsAudioMuted(true);
                      })
                      .catch((mutedError) => {
                        console.log('[DIAG] Audio autoplay prevented even muted:', mutedError);
                        hasPlayedAudioRef.current = false; // Reset flag so it can try again on user interaction
                      });
                  }
                });
            }
          }
        };
        
        // Check if audio is ready to play
        if (audioRef.current.readyState >= 2) { // HAVE_CURRENT_DATA or higher
          console.log('[DIAG] Audio is ready, attempting to play immediately (unmuted)');
          tryPlay();
        } else {
          console.log('[DIAG] Audio not ready yet, readyState:', audioRef.current.readyState, 'waiting for canplay');
          // Wait for audio to be ready
          const handleCanPlay = () => {
            console.log('[DIAG] Audio can now play');
            tryPlay();
            audioRef.current?.removeEventListener('canplay', handleCanPlay);
          };
          audioRef.current.addEventListener('canplay', handleCanPlay);
          
          // Also try to load it
          audioRef.current.load();
        }
      } else {
        console.error('[DIAG] Audio ref is null!');
      }
    }
    
    // Also ensure audio is playing if we're on slide 2+ and it's not playing
    if (currentIndex >= 1 && hasPlayedAudioRef.current && audioRef.current && audioRef.current.paused) {
      console.log('[DIAG] Audio should be playing but is paused, resuming');
      audioRef.current.play().catch((error) => {
        console.log('[DIAG] Audio resume failed:', error);
      });
    }
    
    // Pause audio if going back to first slide
    if (currentIndex === 0 && hasPlayedAudioRef.current) {
      console.log('[DIAG] Pausing audio - going back to first slide');
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    }
    
    // Resume audio if we're on slide 2 or later and it was playing
    if (currentIndex >= 1 && hasPlayedAudioRef.current && audioRef.current && audioRef.current.paused) {
      console.log('[DIAG] Resuming audio on slide', currentIndex);
      // Keep muted state as is - respect user's explicit mute choice
      if (userExplicitlyMutedRef.current) {
        audioRef.current.muted = true;
      }
      audioRef.current.play().catch((error) => {
        console.log('[DIAG] Audio resume failed:', error);
      });
    }
    
    // Sync button state with actual audio muted state whenever slide changes
    // But respect user's explicit mute choice
    if (audioRef.current) {
      // If user explicitly muted, make sure it stays muted
      if (userExplicitlyMutedRef.current && !audioRef.current.muted) {
        audioRef.current.muted = true;
        console.log('[DIAG] Re-muting audio on slide change - user explicitly muted');
      }
      setIsAudioMuted(audioRef.current.muted);
    }
    
  }, [currentIndex]);

  // Preload images for upcoming slides to improve loading performance
  useEffect(() => {
    if (screens.length === 0) return;
    
    // Preload images for next 2 slides ahead (not too aggressive)
    const preloadCount = 2;
    const preloadedImages = new Set<string>();
    
    // Get all common image paths that might be used
    const commonImages = [
      '/daisy_wingate_2.png',
      '/wingate.satelite.png',
      '/daisy_wager.png',
      '/wingi_index.png',
      '/daisy_yellowstone_36.png',
      '/daisy_headshot.png',
      '/daisy_confused.png',
      '/wingate1.jpg',
      '/daisy_avclub.png',
      '/daisy_nychm.png',
      '/daisy_and_dogs.png',
      '/daisy_wingate.png',
      '/daisy_christina.png',
      '/wingatebw1.jpg',
      '/wrapped-wingo.jpeg',
      '/trimbawolf-wrapped-2.png',
    ];
    
    // Preload common images using Image objects (browser will cache them)
    // Only do this once when component mounts or when we're on early slides
    if (currentIndex <= 3) {
      commonImages.forEach(imgSrc => {
        if (!preloadedImages.has(imgSrc)) {
          preloadedImages.add(imgSrc);
          // Use Image object for preloading (non-blocking)
          const img = new Image();
          img.src = imgSrc;
        }
      });
    }
  }, [currentIndex, screens.length]);

  // Global touch event listener for diagnosis and audio unlock
  useEffect(() => {
    const handleGlobalTouchStart = (e: TouchEvent) => {
      console.log('[DIAG] Global touchstart on:', e.target, 'tagName:', (e.target as HTMLElement)?.tagName);
      
      // Mark that user has interacted (for audio unlock)
      if (!hasStarted) {
        setHasStarted(true);
      }
      
      // Try to unlock audio on first user interaction (for mobile autoplay restrictions)
      if (audioRef.current) {
        if (audioRef.current.paused && !hasPlayedAudioRef.current) {
          // Try to play unmuted on user interaction
          console.log('[DIAG] Attempting to play audio unmuted on user interaction');
          audioRef.current.muted = false;
          audioRef.current.play()
            .then(() => {
              console.log('[DIAG] Audio play succeeded on user interaction (unmuted)');
              hasPlayedAudioRef.current = true;
              setIsAudioMuted(false);
            })
            .catch((error) => {
              console.log('[DIAG] Audio play failed unmuted, trying muted:', error);
              // Fallback to muted if unmuted fails
              if (audioRef.current) {
                audioRef.current.muted = true;
                audioRef.current.play()
                  .then(() => {
                    console.log('[DIAG] Audio play succeeded muted, unmuting');
                    hasPlayedAudioRef.current = true;
                    if (audioRef.current) {
                      audioRef.current.muted = false;
                    }
                  })
                  .catch((mutedError) => {
                    console.log('[DIAG] Audio play failed even muted:', mutedError);
                  });
              }
            });
        } else if (!audioRef.current.paused && audioRef.current.muted && !userExplicitlyMutedRef.current) {
          // If audio is playing but muted, only unmute if user hasn't explicitly muted it
          console.log('[DIAG] Unmuting already playing audio on user interaction (not explicitly muted)');
          audioRef.current.muted = false;
          setIsAudioMuted(false);
        }
      }
    };
    const handleGlobalTouchEnd = (e: TouchEvent) => {
      console.log('[DIAG] Global touchend on:', e.target, 'tagName:', (e.target as HTMLElement)?.tagName);
    };
    const handleGlobalClick = (e: MouseEvent) => {
      console.log('[DIAG] Global click on:', e.target, 'tagName:', (e.target as HTMLElement)?.tagName);
      
      // Mark that user has interacted (for audio unlock)
      if (!hasStarted) {
        setHasStarted(true);
      }
      
      // Try to unlock audio on first user interaction
      if (audioRef.current) {
        if (audioRef.current.paused && !hasPlayedAudioRef.current) {
          // Try to play unmuted on user interaction
          console.log('[DIAG] Attempting to play audio unmuted on click interaction');
          audioRef.current.muted = false;
          audioRef.current.play()
            .then(() => {
              console.log('[DIAG] Audio play succeeded on click interaction (unmuted)');
              hasPlayedAudioRef.current = true;
            })
            .catch((error) => {
              console.log('[DIAG] Audio play failed unmuted, trying muted:', error);
              // Fallback to muted if unmuted fails
              if (audioRef.current) {
                audioRef.current.muted = true;
                audioRef.current.play()
                  .then(() => {
                    console.log('[DIAG] Audio play succeeded muted, unmuting');
                    hasPlayedAudioRef.current = true;
                    if (audioRef.current) {
                      audioRef.current.muted = false;
                      setIsAudioMuted(false);
                    }
                  })
                  .catch((mutedError) => {
                    console.log('[DIAG] Audio play failed even muted:', mutedError);
                  });
              }
            });
        } else if (!audioRef.current.paused && audioRef.current.muted && !userExplicitlyMutedRef.current) {
          // If audio is playing but muted, only unmute if user hasn't explicitly muted it
          // Also check if the click was on the audio button itself - if so, don't unmute here
          const target = e.target as HTMLElement;
          const isAudioButton = target.closest('button[aria-label*="audio"]') !== null;
          if (!isAudioButton) {
            console.log('[DIAG] Unmuting already playing audio on click interaction (not explicitly muted)');
            audioRef.current.muted = false;
            setIsAudioMuted(false);
          }
        }
      }
    };

    document.addEventListener('touchstart', handleGlobalTouchStart, true);
    document.addEventListener('touchend', handleGlobalTouchEnd, true);
    document.addEventListener('click', handleGlobalClick, true);

    return () => {
      document.removeEventListener('touchstart', handleGlobalTouchStart, true);
      document.removeEventListener('touchend', handleGlobalTouchEnd, true);
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [currentIndex]);

  // CSS and stacking context diagnostics
  useEffect(() => {
    const logCSSDiagnostics = () => {
      console.log('[DIAG] === CSS and Stacking Context Diagnostics ===');
      
      // Find overlay elements
      const overlayContainer = document.querySelector('[class*="absolute inset-0 z-20"]');
      const leftOverlay = overlayContainer?.querySelector('[class*="absolute left-0"]');
      const rightOverlay = overlayContainer?.querySelector('[class*="absolute right-0"]');
      
      // Find content container
      const contentContainer = document.querySelector('[class*="flex-1 flex items-center justify-center relative z-10"]');
      
      // Find interactive elements
      const links = document.querySelectorAll('a[data-no-navigate="true"]');
      const buttons = document.querySelectorAll('button');
      const videos = document.querySelectorAll('video');
      
      if (overlayContainer) {
        const styles = window.getComputedStyle(overlayContainer as Element);
        console.log('[DIAG] Overlay container:', {
          zIndex: styles.zIndex,
          pointerEvents: styles.pointerEvents,
          position: styles.position,
        });
      }
      
      if (leftOverlay) {
        const styles = window.getComputedStyle(leftOverlay as Element);
        console.log('[DIAG] Left overlay:', {
          zIndex: styles.zIndex,
          pointerEvents: styles.pointerEvents,
          touchAction: styles.touchAction,
          position: styles.position,
        });
      }
      
      if (rightOverlay) {
        const styles = window.getComputedStyle(rightOverlay as Element);
        console.log('[DIAG] Right overlay:', {
          zIndex: styles.zIndex,
          pointerEvents: styles.pointerEvents,
          touchAction: styles.touchAction,
          position: styles.position,
        });
      }
      
      if (contentContainer) {
        const styles = window.getComputedStyle(contentContainer as Element);
        console.log('[DIAG] Content container:', {
          zIndex: styles.zIndex,
          pointerEvents: styles.pointerEvents,
          position: styles.position,
        });
      }
      
      links.forEach((link, i) => {
        const styles = window.getComputedStyle(link);
        console.log(`[DIAG] Link ${i}:`, {
          zIndex: styles.zIndex,
          pointerEvents: styles.pointerEvents,
          touchAction: styles.touchAction,
          position: styles.position,
          tagName: link.tagName,
          href: (link as HTMLAnchorElement).href,
        });
      });
      
      buttons.forEach((button, i) => {
        const styles = window.getComputedStyle(button);
        console.log(`[DIAG] Button ${i}:`, {
          zIndex: styles.zIndex,
          pointerEvents: styles.pointerEvents,
          touchAction: styles.touchAction,
          position: styles.position,
        });
      });
      
      videos.forEach((video, i) => {
        const styles = window.getComputedStyle(video);
        console.log(`[DIAG] Video ${i}:`, {
          zIndex: styles.zIndex,
          pointerEvents: styles.pointerEvents,
          touchAction: styles.touchAction,
          position: styles.position,
        });
      });
      
      console.log('[DIAG] === End CSS Diagnostics ===');
    };
    
    // Detect mobile Chrome
    const isMobileChrome = () => {
      const ua = navigator.userAgent;
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      const isChrome = /Chrome/i.test(ua) && !/Edge|Edg|OPR/i.test(ua);
      return isMobile && isChrome;
    };
    
    // Log browser info
    const logBrowserInfo = () => {
      console.log('[DIAG] === Browser Information ===');
      console.log('[DIAG] User Agent:', navigator.userAgent);
      console.log('[DIAG] Is Mobile Chrome:', isMobileChrome());
      console.log('[DIAG] Touch Support:', 'ontouchstart' in window);
      console.log('[DIAG] Max Touch Points:', navigator.maxTouchPoints || 0);
      console.log('[DIAG] Viewport Width:', window.innerWidth);
      console.log('[DIAG] Viewport Height:', window.innerHeight);
      console.log('[DIAG] Device Pixel Ratio:', window.devicePixelRatio);
      console.log('[DIAG] === End Browser Information ===');
    };
    
    // Expose diagnostic function to window for manual calling
    (window as any).__WRAPPED_DIAGNOSTICS = {
      logCSS: logCSSDiagnostics,
      logDOMStructure: () => {
        console.log('[DIAG] === DOM Structure ===');
        const wrapper = document.querySelector('[class*="h-full w-full bg-gradient-to-br"]');
        if (wrapper) {
          console.log('[DIAG] Wrapper element:', wrapper);
          console.log('[DIAG] Wrapper children:', Array.from(wrapper.children).map((child, i) => ({
            index: i,
            tagName: child.tagName,
            className: child.className,
            zIndex: window.getComputedStyle(child).zIndex,
            pointerEvents: window.getComputedStyle(child).pointerEvents,
          })));
        }
        console.log('[DIAG] === End DOM Structure ===');
      },
      logBrowserInfo,
      isMobileChrome,
      summary: () => {
        console.log('[DIAG] ========================================');
        console.log('[DIAG] WRAPPED PAGE DIAGNOSTIC SUMMARY');
        console.log('[DIAG] ========================================');
        logBrowserInfo();
        logCSSDiagnostics();
        (window as any).__WRAPPED_DIAGNOSTICS.logDOMStructure();
        console.log('[DIAG] ========================================');
        console.log('[DIAG] To disable overlay for testing, run:');
        console.log('[DIAG]   window.__DISABLE_WRAPPED_OVERLAY = true');
        console.log('[DIAG] To re-enable overlay, run:');
        console.log('[DIAG]   window.__DISABLE_WRAPPED_OVERLAY = false');
        console.log('[DIAG] ========================================');
      },
    };
    
    // Log browser info on mount
    logBrowserInfo();
    
    // Log diagnostics after a short delay to ensure DOM is ready
    const timeout = setTimeout(logCSSDiagnostics, 1000);
    
    // Also log when slide changes
    const interval = setInterval(logCSSDiagnostics, 5000);
    
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
      delete (window as any).__WRAPPED_DIAGNOSTICS;
    };
  }, [currentIndex]);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const animatingIndexRef = useRef<number>(-1); // Track animating index in ref for immediate checks
  const hasInitializedRef = useRef<boolean>(false); // Track if we've completed initialization

  // Consolidated effect: handles progress bar animation and auto-advance
  useEffect(() => {
    if (screens.length === 0) return;

    // Clear any existing timers immediately
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (progressTimerRef.current) {
      clearTimeout(progressTimerRef.current);
      progressTimerRef.current = null;
    }

    // Initialize progress widths array if needed
    // Check if we need to initialize (only check, don't react to progressWidths changes)
    const needsInit = !hasInitializedRef.current && screens.length > 0 && progressWidths.length !== screens.length;
    
    if (needsInit) {
      const newWidths = new Array(screens.length).fill(0);
      for (let i = 0; i < currentIndex; i++) {
        newWidths[i] = 100;
      }
      if (currentIndex < screens.length) {
        newWidths[currentIndex] = 0; // Start at 0 to animate
      }
      
      // Set progress widths first
      setProgressWidths(newWidths);
      prevIndexRef.current = currentIndex;
      
      // Mark initial slide as animating (both state and ref) - do this after setting widths
      // Use double requestAnimationFrame to ensure CSS transition is applied before starting animation
      requestAnimationFrame(() => {
        // Set animating state after first frame so CSS transition is ready
        animatingIndexRef.current = currentIndex;
        setAnimatingIndex(currentIndex);
        
        // Second frame to ensure transition CSS is fully applied
        requestAnimationFrame(() => {
        // Start animation for initial slide
        if (currentIndex < screens.length) {
          const targetIndex = currentIndex;
          const initSlideDuration = getSlideDuration(currentIndex);
          
          // Trigger the width animation to 100% after a brief delay to ensure transition is active
          progressTimerRef.current = setTimeout(() => {
            if (prevIndexRef.current === targetIndex) {
              setProgressWidths(prev => {
                const updated = [...prev];
                if (updated.length === screens.length && targetIndex < screens.length) {
                  updated[targetIndex] = 100;
                }
                return updated;
              });
              // Clear animating flag after animation duration completes
              setTimeout(() => {
                if (animatingIndexRef.current === targetIndex) {
                  animatingIndexRef.current = -1;
                  setAnimatingIndex(-1);
                }
              }, initSlideDuration);
            }
            }, 50); // Brief delay to ensure transition is active
          
            // Set up auto-advance - always auto-advance, no button needed
          if (canNavigate && currentIndex < screens.length - 1) {
            autoAdvanceTimerRef.current = setTimeout(() => {
              handleNext();
            }, initSlideDuration);
          } else if (currentIndex === screens.length - 1 && onComplete) {
            autoAdvanceTimerRef.current = setTimeout(() => {
              onComplete();
            }, initSlideDuration);
          }
        }
        });
      });
      
      // Mark as initialized
      hasInitializedRef.current = true;
      
      return () => {
        if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
        if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      };
    }
    
    // Skip regular navigation logic if we haven't initialized yet
    // BUT still set up auto-advance timer for first slide
    if (!hasInitializedRef.current) {
      // Set up auto-advance for first slide even during initialization
      const slideDuration = getSlideDuration(currentIndex);
      if (canNavigate && currentIndex < screens.length - 1) {
        autoAdvanceTimerRef.current = setTimeout(() => {
          handleNext();
        }, slideDuration);
      }
      // Also ensure progress bar animation is set up for first slide
      if (currentIndex === 0 && progressWidths.length === screens.length) {
        animatingIndexRef.current = 0;
        setAnimatingIndex(0);
      }
      return;
    }

    // Determine if we're going forward or backward
    const isGoingForward = currentIndex > prevIndexRef.current;
    const isGoingBackward = currentIndex < prevIndexRef.current;
    const isSkipping = Math.abs(currentIndex - prevIndexRef.current) > 1;
    
    // Build new progress widths array - ALWAYS start fresh to ensure clean state
    const newWidths = new Array(screens.length).fill(0);
    
    if (isGoingForward) {
      // Going forward: set ALL slides before current to 100% immediately (no animation)
      // Previous slides are done, so they should be fully yellow with no transition
      for (let i = 0; i < currentIndex; i++) {
        newWidths[i] = 100;
      }
      // Current slide: if skipping, set to 100% immediately; otherwise start at 0 for animation
      if (currentIndex < screens.length) {
        newWidths[currentIndex] = isSkipping ? 100 : 0;
      }
      // All future slides stay at 0
    } else if (isGoingBackward) {
      // Going backward: set all slides up to and including current to 100% immediately (no animation)
      for (let i = 0; i <= currentIndex; i++) {
        newWidths[i] = 100;
      }
      // All future slides reset to 0
    } else {
      // Same index (shouldn't happen, but handle it)
      for (let i = 0; i < currentIndex; i++) {
        newWidths[i] = 100;
      }
      if (currentIndex < screens.length) {
        // Preserve current slide's progress if it exists, otherwise start at 0
        newWidths[currentIndex] = (progressWidths.length > currentIndex && progressWidths[currentIndex] !== undefined) 
          ? progressWidths[currentIndex] 
          : 0;
      }
    }
    
    // Update progress widths immediately - use direct assignment for immediate state update
    setProgressWidths(newWidths);
    prevIndexRef.current = currentIndex;
    
    // Handle animation logic based on navigation direction
    if (isGoingForward && !isGoingBackward && !isSkipping) {
      // Going forward by exactly 1: current slide should animate from 0 to 100%
      // Mark this slide as animating
      const targetIndex = currentIndex;
      animatingIndexRef.current = targetIndex;
      setAnimatingIndex(targetIndex);
      
      // Trigger the width animation to 100% after a brief delay to ensure state is set
      progressTimerRef.current = setTimeout(() => {
        // Only animate if we're still on the same slide
        if (prevIndexRef.current === targetIndex) {
          setProgressWidths(prev => {
            const updated = [...prev];
            if (updated.length === screens.length && targetIndex < screens.length) {
              updated[targetIndex] = 100;
            }
            return updated;
          });
          // Clear animating flag after animation duration completes
          const slideDuration = getSlideDuration(targetIndex);
          setTimeout(() => {
            if (animatingIndexRef.current === targetIndex) {
              animatingIndexRef.current = -1;
              setAnimatingIndex(-1);
            }
          }, slideDuration);
        }
      }, 50);
    } else {
      // When going backward or skipping, no animation - clear any pending animations
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      animatingIndexRef.current = -1;
      setAnimatingIndex(-1);
    }

    // Set up auto-advance timer - always auto-advance, no button needed
    const slideDuration = getSlideDuration(currentIndex);
    if (canNavigate && currentIndex < screens.length - 1) {
      autoAdvanceTimerRef.current = setTimeout(() => {
        handleNext();
      }, slideDuration);
    } else if (currentIndex === screens.length - 1 && onComplete) {
      autoAdvanceTimerRef.current = setTimeout(() => {
        onComplete();
      }, slideDuration);
    }
    
    return () => {
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    };
  }, [currentIndex, screens.length, canNavigate, onComplete, handleNext, getSlideDuration]);

  return (
    <div 
      className="h-full w-full bg-gradient-to-br from-black via-gray-900 to-black flex flex-col relative overflow-hidden"
      style={{
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        touchAction: 'pan-y',
        overscrollBehavior: 'none',
      }}
    >
      {/* Audio toggle button - subtle, top right, show on all slides */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          // Prevent global handlers from interfering
          const wasExplicitlyMuted = userExplicitlyMutedRef.current;
          handleAudioToggle();
          // Restore explicit mute flag after toggle if needed
          setTimeout(() => {
            if (wasExplicitlyMuted && audioRef.current && audioRef.current.muted) {
              userExplicitlyMutedRef.current = true;
            }
          }, 0);
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          e.preventDefault();
          // Prevent global handlers from interfering
          const wasExplicitlyMuted = userExplicitlyMutedRef.current;
          handleAudioToggle();
          // Restore explicit mute flag after toggle if needed
          setTimeout(() => {
            if (wasExplicitlyMuted && audioRef.current && audioRef.current.muted) {
              userExplicitlyMutedRef.current = true;
            }
          }, 0);
        }}
        className="absolute top-4 right-3 z-40 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-all backdrop-blur-sm border border-yellow-400/30"
        aria-label={isAudioMuted ? "Unmute audio" : "Mute audio"}
        style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
      >
        {isAudioMuted ? (
          <svg className="w-5 h-5 text-yellow-200/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-yellow-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
        )}
      </button>

      {/* Top progress bar - Instagram/TikTok style */}
      <div className="absolute top-0 left-0 right-0 z-30 px-2 pt-2 pb-1 safe-area-inset-top">
        <div className="flex gap-1 max-w-full md:max-w-[480px] mx-auto relative">
          {screens.map((_, index) => (
            <div
              key={index}
              className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
            >
              <div
                className={`h-full ${
                  index <= currentIndex ? 'bg-yellow-400' : 'bg-white/30'
                }`}
                style={{
                  width: `${progressWidths[index] || 0}%`,
                  // Only animate if this is the current slide AND it's marked as animating
                  // When animatingIndex is -1, no slide should animate (prevents multiple animations)
                  transition: (index === currentIndex && animatingIndex === currentIndex && animatingIndex !== -1)
                    ? `width ${getSlideDuration(index)}ms linear` 
                    : 'none', // No transition for skipped slides or when going backward - use 'none' to completely disable
                }}
              />
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center relative z-10 overflow-y-auto" style={{ minHeight: 0, maxHeight: '100%', paddingTop: '12px' }}>
        <div className="w-full max-w-full md:max-w-[480px] mx-auto relative px-3 md:px-4 py-2 flex items-center justify-center min-h-full">
          <div
            key={currentIndex}
            className="w-full flex items-center justify-center py-2"
            style={{
              minHeight: '100%',
              // Most slides use normal fadeIn, only special ones get fancy transitions
              animation: currentIndex === Math.floor(screens.length / 2)
                ? 'slideInFromRight 0.4s ease-out'  // Middle slide gets slide
                : 'fadeIn 0.4s ease-out',  // All others use normal fade (including first slide)
            }}
          >
            {screens[currentIndex]}
          </div>
        </div>
      </div>


      {/* Clickable areas for navigation - left side goes back, right side goes forward */}
      {/* Only intercept clicks if they're not on interactive elements */}
      {/* DIAGNOSTIC: Set window.__DISABLE_WRAPPED_OVERLAY = true to disable overlay */}
      {/* Navigation overlay - show on all slides */}
      <div className="absolute inset-0 z-20 pointer-events-none" style={{ display: overlayDisabled ? 'none' : 'block' }}>
        <div 
          className="absolute left-0 top-0 bottom-0 w-1/2 cursor-pointer"
          onClick={(e) => {
            console.log('[DIAG] Left overlay onClick fired');
            
            // Get click coordinates
            const x = e.clientX;
            const y = e.clientY;
            console.log('[DIAG] Click coordinates:', x, y);
            
            // Get all elements at this point
            const elementsAtPoint = document.elementsFromPoint(x, y);
            console.log('[DIAG] All elements at click point:', elementsAtPoint);
            
            // Filter out the overlay divs
            const overlayDivs = [e.currentTarget as HTMLElement];
            const elementUnderOverlay = elementsAtPoint.find(el => {
              const isOverlay = overlayDivs.some(overlay => overlay.contains(el) || overlay === el);
              return !isOverlay;
            });
            console.log('[DIAG] Element under overlay:', elementUnderOverlay);
            
            if (!elementUnderOverlay) {
              console.log('[DIAG] No element under overlay, calling handlePrev()');
              handlePrev();
              return;
            }
            
            // Check if the element under overlay is interactive
            const clickedElement = elementUnderOverlay.closest('a, button, [role="button"], video, [onclick], [data-no-navigate]');
            console.log('[DIAG] closest() result from elementUnderOverlay:', clickedElement);
            
            if (clickedElement) {
              console.log('[DIAG] Found interactive element, triggering click');
              e.preventDefault();
              e.stopPropagation();
              (clickedElement as HTMLElement).click();
              return;
            }
            
            // Also check if elementUnderOverlay itself is interactive
            if (elementUnderOverlay.tagName === 'A' || elementUnderOverlay.tagName === 'BUTTON' || 
                elementUnderOverlay.tagName === 'VIDEO' || elementUnderOverlay.hasAttribute('data-no-navigate') ||
                elementUnderOverlay.hasAttribute('onclick')) {
              console.log('[DIAG] Element itself is interactive, triggering click');
              e.preventDefault();
              e.stopPropagation();
              (elementUnderOverlay as HTMLElement).click();
              return;
            }
            
            // Check parent chain
            let parent = elementUnderOverlay.parentElement;
            while (parent && parent !== e.currentTarget) {
              if (parent.tagName === 'A' || parent.hasAttribute('data-no-navigate')) {
                console.log('[DIAG] Found link in parent chain, triggering click');
                e.preventDefault();
                e.stopPropagation();
                (parent as HTMLElement).click();
                return;
              }
              parent = parent.parentElement;
            }
            
            console.log('[DIAG] No interactive element found, calling handlePrev()');
            handlePrev();
          }}
          onTouchStart={(e) => {
            console.log('[DIAG] Left overlay onTouchStart fired');
            
            // Get the touch coordinates
            const touch = e.touches[0];
            const x = touch.clientX;
            const y = touch.clientY;
            console.log('[DIAG] Touch coordinates:', x, y);
            
            // Get all elements at this point (overlay will be first, but we want what's underneath)
            const elementsAtPoint = document.elementsFromPoint(x, y);
            console.log('[DIAG] All elements at touch point:', elementsAtPoint);
            
            // Filter out the overlay divs - find first element that's not an overlay
            const overlayDivs = [e.currentTarget as HTMLElement];
            const elementUnderOverlay = elementsAtPoint.find(el => {
              const isOverlay = overlayDivs.some(overlay => overlay.contains(el) || overlay === el);
              return !isOverlay;
            });
            console.log('[DIAG] Element under overlay:', elementUnderOverlay);
            console.log('[DIAG] Element tagName:', elementUnderOverlay?.tagName);
            
            if (!elementUnderOverlay) {
              console.log('[DIAG] No element under overlay, calling handlePrev()');
              handlePrev();
              return;
            }
            
            // Check if the element under overlay is interactive
            const clickedElement = elementUnderOverlay.closest('a, button, [role="button"], video, [onclick], [data-no-navigate]');
            console.log('[DIAG] closest() result from elementUnderOverlay:', clickedElement);
            
            if (clickedElement) {
              console.log('[DIAG] Found interactive element, triggering click');
              handleInteractiveElementClick(clickedElement as HTMLElement, e);
              return;
            }
            
            // Also check if elementUnderOverlay itself is interactive
            if (elementUnderOverlay.tagName === 'A' || elementUnderOverlay.tagName === 'BUTTON' || 
                elementUnderOverlay.tagName === 'VIDEO' || elementUnderOverlay.hasAttribute('data-no-navigate') ||
                elementUnderOverlay.hasAttribute('onclick')) {
              console.log('[DIAG] Element itself is interactive, triggering click');
              handleInteractiveElementClick(elementUnderOverlay as HTMLElement, e);
              return;
            }
            
            // Check parent chain
            let parent = elementUnderOverlay.parentElement;
            while (parent && parent !== e.currentTarget) {
              if (parent.tagName === 'A' || parent.hasAttribute('data-no-navigate')) {
                console.log('[DIAG] Found link in parent chain, triggering click');
                handleInteractiveElementClick(parent as HTMLElement, e);
                return;
              }
              parent = parent.parentElement;
            }
            
            console.log('[DIAG] No interactive element found, calling handlePrev()');
            handlePrev();
          }}
          onTouchMove={(e) => {
            console.log('[DIAG] Left overlay onTouchMove fired');
          }}
          onTouchEnd={(e) => {
            console.log('[DIAG] Left overlay onTouchEnd fired');
            console.log('[DIAG] Touch count:', e.changedTouches.length);
          }}
          onTouchCancel={(e) => {
            console.log('[DIAG] Left overlay onTouchCancel fired');
          }}
          style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
        />
        <div 
          className="absolute right-0 top-0 bottom-0 w-1/2 cursor-pointer"
          onClick={(e) => {
            console.log('[DIAG] Right overlay onClick fired');
            
            // Get click coordinates
            const x = e.clientX;
            const y = e.clientY;
            console.log('[DIAG] Click coordinates:', x, y);
            
            // Get all elements at this point
            const elementsAtPoint = document.elementsFromPoint(x, y);
            console.log('[DIAG] All elements at click point:', elementsAtPoint);
            
            // Filter out the overlay divs
            const overlayDivs = [e.currentTarget as HTMLElement];
            const elementUnderOverlay = elementsAtPoint.find(el => {
              const isOverlay = overlayDivs.some(overlay => overlay.contains(el) || overlay === el);
              return !isOverlay;
            });
            console.log('[DIAG] Element under overlay:', elementUnderOverlay);
            
            if (!elementUnderOverlay) {
              console.log('[DIAG] No element under overlay, calling handleNext()');
              handleNext();
              return;
            }
            
            // Check if the element under overlay is interactive
            const clickedElement = elementUnderOverlay.closest('a, button, [role="button"], video, [onclick], [data-no-navigate]');
            console.log('[DIAG] closest() result from elementUnderOverlay:', clickedElement);
            
            if (clickedElement) {
              console.log('[DIAG] Found interactive element, triggering click');
              e.preventDefault();
              e.stopPropagation();
              (clickedElement as HTMLElement).click();
              return;
            }
            
            // Also check if elementUnderOverlay itself is interactive
            if (elementUnderOverlay.tagName === 'A' || elementUnderOverlay.tagName === 'BUTTON' || 
                elementUnderOverlay.tagName === 'VIDEO' || elementUnderOverlay.hasAttribute('data-no-navigate') ||
                elementUnderOverlay.hasAttribute('onclick')) {
              console.log('[DIAG] Element itself is interactive, triggering click');
              e.preventDefault();
              e.stopPropagation();
              (elementUnderOverlay as HTMLElement).click();
              return;
            }
            
            // Check parent chain
            let parent = elementUnderOverlay.parentElement;
            while (parent && parent !== e.currentTarget) {
              if (parent.tagName === 'A' || parent.hasAttribute('data-no-navigate')) {
                console.log('[DIAG] Found link in parent chain, triggering click');
                e.preventDefault();
                e.stopPropagation();
                (parent as HTMLElement).click();
                return;
              }
              parent = parent.parentElement;
            }
            
            console.log('[DIAG] No interactive element found, calling handleNext()');
            handleNext();
          }}
          onTouchStart={(e) => {
            console.log('[DIAG] Right overlay onTouchStart fired');
            
            // Get the touch coordinates
            const touch = e.touches[0];
            const x = touch.clientX;
            const y = touch.clientY;
            console.log('[DIAG] Touch coordinates:', x, y);
            
            // Get all elements at this point (overlay will be first, but we want what's underneath)
            const elementsAtPoint = document.elementsFromPoint(x, y);
            console.log('[DIAG] All elements at touch point:', elementsAtPoint);
            
            // Filter out the overlay divs - find first element that's not an overlay
            const overlayDivs = [e.currentTarget as HTMLElement];
            const elementUnderOverlay = elementsAtPoint.find(el => {
              const isOverlay = overlayDivs.some(overlay => overlay.contains(el) || overlay === el);
              return !isOverlay;
            });
            console.log('[DIAG] Element under overlay:', elementUnderOverlay);
            console.log('[DIAG] Element tagName:', elementUnderOverlay?.tagName);
            
            if (!elementUnderOverlay) {
              console.log('[DIAG] No element under overlay, calling handleNext()');
              handleNext();
              return;
            }
            
            // Check if the element under overlay is interactive
            const clickedElement = elementUnderOverlay.closest('a, button, [role="button"], video, [onclick], [data-no-navigate]');
            console.log('[DIAG] closest() result from elementUnderOverlay:', clickedElement);
            
            if (clickedElement) {
              console.log('[DIAG] Found interactive element, triggering click');
              handleInteractiveElementClick(clickedElement as HTMLElement, e);
              return;
            }
            
            // Also check if elementUnderOverlay itself is interactive
            if (elementUnderOverlay.tagName === 'A' || elementUnderOverlay.tagName === 'BUTTON' || 
                elementUnderOverlay.tagName === 'VIDEO' || elementUnderOverlay.hasAttribute('data-no-navigate') ||
                elementUnderOverlay.hasAttribute('onclick')) {
              console.log('[DIAG] Element itself is interactive, triggering click');
              handleInteractiveElementClick(elementUnderOverlay as HTMLElement, e);
              return;
            }
            
            // Check parent chain
            let parent = elementUnderOverlay.parentElement;
            while (parent && parent !== e.currentTarget) {
              if (parent.tagName === 'A' || parent.hasAttribute('data-no-navigate')) {
                console.log('[DIAG] Found link in parent chain, triggering click');
                handleInteractiveElementClick(parent as HTMLElement, e);
                return;
              }
              parent = parent.parentElement;
            }
            
            console.log('[DIAG] No interactive element found, calling handleNext()');
            handleNext();
          }}
          onTouchMove={(e) => {
            console.log('[DIAG] Right overlay onTouchMove fired');
          }}
          onTouchEnd={(e) => {
            console.log('[DIAG] Right overlay onTouchEnd fired');
            console.log('[DIAG] Touch count:', e.changedTouches.length);
          }}
          onTouchCancel={(e) => {
            console.log('[DIAG] Right overlay onTouchCancel fired');
          }}
          style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}
        />
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3) translateY(20px);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
          }
        }
        /* Safe area insets for mobile browsers */
        .safe-area-inset-top {
          padding-top: max(8px, env(safe-area-inset-top));
        }
        .safe-area-inset-bottom {
          padding-bottom: max(12px, env(safe-area-inset-bottom));
        }
      `}</style>
    </div>
  );
});

WrappedScreenWrapper.displayName = 'WrappedScreenWrapper';

